import { useCallback } from 'react';
import Basemap from '@arcgis/core/Basemap';
import { useEsriMapContext } from '../EsriMapContext';
import { EsriBasemapId } from '../types';
import type { BasemapOption } from '../types';

// Features #14–25, #64: basemap switching

/**
 * Hook for managing ESRI basemap switching.
 *
 * Provides methods to:
 * - Set the basemap using ArcGIS REST API basemap style IDs
 * - Get the current basemap ID
 * - List available basemaps for UI selection
 *
 * @example
 * ```tsx
 * const basemap = useBasemap();
 * basemap.setBasemap(EsriBasemapId.Navigation);
 * const currentId = basemap.getBasemap();
 * const options = basemap.getAvailableBasemaps();
 * ```
 */
export const useBasemap = () => {
	const { view, map } = useEsriMapContext();

	/**
	 * Sets the map's basemap to the specified ArcGIS REST API basemap style ID.
	 *
	 * Uses the basemap styles REST API (`new Basemap({ style: { id } })`) for all
	 * `EsriBasemapId` values (e.g. `'arcgis/navigation'`, `'open/streets'`).
	 *
	 * @param id - The `EsriBasemapId` style ID to switch to
	 */
	const setBasemap = useCallback(
		(id: EsriBasemapId): void => {
			if (!view?.map) {
				console.warn('useBasemap.setBasemap: view or map not ready');
				return;
			}

			try {
				view.map.basemap = new Basemap({ style: { id } });
			} catch (error) {
				console.error(`Failed to set basemap to '${id}':`, error);
			}
		},
		[view, map],
	);

	/**
	 * Gets the current basemap style ID.
	 *
	 * @returns The style ID of the current basemap, or an empty string if unavailable
	 */
	const getBasemap = useCallback((): string => {
		if (!view?.map?.basemap) {
			return '';
		}

		const basemap = view.map.basemap as any;
		return basemap.style?.id ?? basemap.id ?? basemap.portalItem?.id ?? '';
	}, [view, map]);

	/**
	 * Gets a list of available basemaps for UI selection.
	 *
	 * @returns Array of basemap options with ID, label, and optional thumbnail URL
	 */
	const getAvailableBasemaps = useCallback((): BasemapOption[] => {
		return [
			{ id: EsriBasemapId.Navigation, label: 'Streets' },
			{ id: EsriBasemapId.Imagery, label: 'Satellite' },
			{ id: EsriBasemapId.OpenHybrid, label: 'Hybrid' },
			{ id: EsriBasemapId.Topographic, label: 'Topographic' },
			{ id: EsriBasemapId.DarkGray, label: 'Dark Gray' },
			{ id: EsriBasemapId.LightGray, label: 'Light Gray' },
			{ id: EsriBasemapId.StreetsNight, label: 'Streets Night' },
			{ id: EsriBasemapId.Terrain, label: 'Terrain' },
			{ id: EsriBasemapId.Oceans, label: 'Oceans' },
			{ id: EsriBasemapId.ChartedTerritory, label: 'Charted Territory' },
			{ id: EsriBasemapId.OpenStreets, label: 'OpenStreetMap' },
			{ id: EsriBasemapId.StreetsWithRelief, label: 'Streets with Relief' },
		];
	}, []);

	return {
		setBasemap,
		getBasemap,
		getAvailableBasemaps,
	};
};

export type BasemapControls = ReturnType<typeof useBasemap>;
